import {
  AvaaioLanguageCode,
  normalizeAvaaioLanguage,
} from "./avaaioLanguage";

export const AVAAIO_UI_COPY = {
  en: {
    dashboard: {
      loadingSystem: "NEURAL INTELLIGENCE SYSTEM",
      loadingAva: "LOADING AVA...",
      lockedFeature:
        "{feature} is locked on the Free plan. Upgrade to Pro to unlock it.",
    },
    settings: {
      eyebrow: "AVAAIO Control System",
      title: "SETTINGS",
      subtitle:
        "Configure your AVAAIO environment, open connected modules, and move through your account systems from one clean control surface.",
      lockedNotice:
        "🔒 Upgrade required to access this feature",
      backDashboard: "← Return to AVAAIO Dashboard",
      cards: {
        profile: {
          title: "Profile",
          subtitle:
            "View your account identity, display information, and user details.",
        },
        system: {
          title: "System",
          subtitle:
            "Manage system settings, session information, connected modules, and access.",
        },
        account: {
          title: "Account",
          subtitle:
            "Manage login, email, password, membership, and account security controls.",
        },
        language: {
          title: "Language & Region",
          subtitle:
            "Choose the language AVAAIO remembers for your account and interface.",
        },
        help: {
          title: "HELP",
          subtitle:
            "Open help, guidance, and platform resources.",
        },
        support: {
          title: "Support",
          subtitle:
            "Contact platform support and open support options.",
        },
        theme: {
          title: "Theme Studio",
          subtitle:
            "Adjust platform appearance and dashboard theme controls.",
          freeSubtitle:
            "Upgrade to Pro to unlock Ava theme customization.",
        },
        entertainment: {
          title: "Entertainment",
          subtitle:
            "Open media and interactive experience modules.",
        },
        games: {
          title: "Games Hub",
          subtitle:
            "Launch chess, checkers, and game modes.",
        },
        education: {
          title: "Education",
          subtitle:
            "Learn with Ava through tutoring, flashcards, math boards, and guided lessons.",
        },
      },
    },
    language: {
      eyebrow: "AVAAIO LANGUAGE SYSTEM",
      title: "Language & Region",
      subtitle:
        "Choose the language AVAAIO should remember for your account and interface.",
      preferred: "Preferred language",
      selected: "Selected",
      note:
        "This setting does not change Ava's conversation or microphone language. Multilingual conversations and automatic language switching are available with Pro.",
      saving: "Saving...",
      saved: "Language Saved",
      save: "Save Language",
      back: "← Back to Settings",
      success:
        "Language preference saved. Applying it now...",
      error:
        "Language preference could not be saved.",
    },
    plan: {
      accountFallback: "Account",
      title: "Account Access",
      statusPrefix: "Status for",
      subtitle:
        "Free users can preview locked features here. Pro unlocks Ava Vision, expanded features, and higher usage access.",
      freeTitle: "Free",
      freeDescription:
        "Limited Ava access, locked premium features, and restricted usage. Suitable for entry access and previewing Pro features.",
      proTitle: "Pro",
      proDescription:
        "Unlock Ava Vision, fuller features, richer account access, and the premium Ava experience.",
      adminTitle: "Account",
        adminDescription: "Your current account access is active.",
      active: "Active",
      unlockPro: "Unlock Pro",
      iosOnly: "Purchases are available only in the iOS app.",
      purchaseError: "Unable to start purchase.",
    },
    mobile: {
      openControls: "Open Ava controls",
      controls: "Ava controls",
      profile: "Profile",
      history: "History",
      system: "System",
      visionLockedTitle:
        "Ava Vision is locked on the Free plan. Upgrade to Pro to unlock it.",
      disableVision: "Disable Ava Vision",
      enableVision: "Enable Ava Vision",
      visionLocked: "Ava Vision: Locked",
      visionOn: "Ava Vision: On",
      visionOff: "Ava Vision: Off",
      motionPaidAlert:
        "Ava Motion Tracking is available with a paid AVAAIO subscription.",
      motionPaidTitle:
        "Ava Motion Tracking requires a paid AVAAIO subscription.",
      disableMotion: "Disable Ava Motion Track",
      enableMotion: "Enable Ava Motion Track",
      motionLocked: "Ava Motion Track: Locked",
      motionOn: "Ava Motion Track: On",
      motionOff: "Ava Motion Track: Off",
      logout: "Logout",
    },
    chat: {
      disclaimer:
        "AI responses may be inaccurate. Verify important information.",
      logout: "Logout",
      menuTitle: "AVA MENU",
      profile: "Profile",
      account: "Account",
      entertainment: "Entertainment",
      games: "Games",
      themeColors: "Theme & Colors",
      logoutReturn: "Logout & Return to Login",
      inputPlaceholder: "Type your message...",
      thinking: "Thinking",
      thinkingTitle: "Ava is thinking",
      offlineMicError:
        "The offline microphone encountered an error while recording. Try again.",
      wakeStandbyTitle:
        "Wake word standby — say “Ava, can you hear me?”",
      micOffTitle: "Turn microphone off",
      micOnTitle:
        "Turn microphone on and enable wake word",
      locale: "en-US",
    },
    history: {
      ariaLabel: "Chat history",
      title: "Ava Memory Stream",
      sessionCount:
        "{filtered} / {total} messages in this session",
      searchPlaceholder:
        "Search this conversation...",
      noMatches:
        "No matching chat history yet.",
      you: "You",
      ava: "Ava",
      close: "Close",
    },
    bottomNav: {
      backSettings: "← Back to Settings",
      backDashboard: "Back to Dashboard",
    },
    accountMenu: {
      options: "Account options",
      maintenance: "Maintenance",
      confirm:
        "This will disconnect your account, remove Ava profile settings from this device, and sign you out. Continue?",
      disconnect: "Disconnect & Wipe — Sign out",
      detail:
        "Removes the local Ava profile and clears this device's session.",
    },
  },

  es: {
    dashboard: {
      loadingSystem: "SISTEMA DE INTELIGENCIA NEURONAL",
      loadingAva: "CARGANDO AVA...",
      lockedFeature:
        "{feature} está bloqueado en el plan Gratis. Actualiza a Pro para desbloquearlo.",
    },
    settings: {
      eyebrow: "Sistema de control AVAAIO",
      title: "AJUSTES",
      subtitle:
        "Configura tu entorno AVAAIO, abre los módulos conectados y administra los sistemas de tu cuenta desde un solo panel.",
      lockedNotice:
        "🔒 Se requiere una actualización para acceder a esta función",
      backDashboard: "← Volver al panel de AVAAIO",
      cards: {
        profile: {
          title: "Perfil",
          subtitle:
            "Consulta la identidad de tu cuenta, la información visible y los datos del usuario.",
        },
        system: {
          title: "Sistema",
          subtitle:
            "Administra los ajustes del sistema, la sesión, los módulos conectados y el acceso.",
        },
        account: {
          title: "Cuenta",
          subtitle:
            "Administra el inicio de sesión, correo, contraseña, membresía y seguridad de la cuenta.",
        },
        language: {
          title: "Idioma y región",
          subtitle:
            "Elige el idioma que AVAAIO recordará para tu cuenta y la interfaz.",
        },
        help: {
          title: "AYUDA",
          subtitle:
            "Abre la ayuda, las guías y los recursos de la plataforma.",
        },
        support: {
          title: "Soporte",
          subtitle:
            "Contacta con soporte y abre las opciones de asistencia.",
        },
        theme: {
          title: "Estudio de temas",
          subtitle:
            "Ajusta la apariencia de la plataforma y los controles visuales del panel.",
          freeSubtitle:
            "Actualiza a Pro para desbloquear la personalización visual de Ava.",
        },
        entertainment: {
          title: "Entretenimiento",
          subtitle:
            "Abre módulos multimedia y experiencias interactivas.",
        },
        games: {
          title: "Centro de juegos",
          subtitle:
            "Inicia ajedrez, damas y otros modos de juego.",
        },
        education: {
          title: "Educación",
          subtitle:
            "Aprende con Ava mediante tutorías, tarjetas, matemáticas y lecciones guiadas.",
        },
      },
    },
    language: {
      eyebrow: "SISTEMA DE IDIOMA AVAAIO",
      title: "Idioma y región",
      subtitle:
        "Elige el idioma que AVAAIO debe recordar para tu cuenta y la interfaz.",
      preferred: "Idioma preferido",
      selected: "Seleccionado",
      note:
        "Este ajuste no cambia el idioma de conversación ni del micrófono de Ava. Las conversaciones multilingües y el cambio automático de idioma están disponibles con Pro.",
      saving: "Guardando...",
      saved: "Idioma guardado",
      save: "Guardar idioma",
      back: "← Volver a Ajustes",
      success:
        "Preferencia de idioma guardada. Aplicándola ahora...",
      error:
        "No se pudo guardar la preferencia de idioma.",
    },
    plan: {
      accountFallback: "Cuenta",
      title: "Acceso de la cuenta",
      statusPrefix: "Estado de",
      subtitle:
        "Los usuarios Gratis pueden ver aquí las funciones bloqueadas. Pro desbloquea Ava Vision, funciones ampliadas y mayor acceso de uso.",
      freeTitle: "Gratis",
      freeDescription:
        "Acceso limitado a Ava, funciones premium bloqueadas y uso restringido. Adecuado para comenzar y conocer las funciones Pro.",
      proTitle: "Pro",
      proDescription:
        "Desbloquea Ava Vision, más funciones, mayor acceso a la cuenta y la experiencia premium de Ava.",
      adminTitle: "Cuenta",
        adminDescription: "El acceso disponible para esta cuenta está activo.",
      active: "Activo",
      unlockPro: "Desbloquear Pro",
      iosOnly:
        "Las compras solo están disponibles en la aplicación para iOS.",
      purchaseError: "No se pudo iniciar la compra.",
    },
    mobile: {
      openControls: "Abrir controles de Ava",
      controls: "Controles de Ava",
      profile: "Perfil",
      history: "Historial",
      system: "Sistema",
      visionLockedTitle:
        "Ava Vision está bloqueado en el plan Gratis. Actualiza a Pro para desbloquearlo.",
      disableVision: "Desactivar Ava Vision",
      enableVision: "Activar Ava Vision",
      visionLocked: "Ava Vision: Bloqueado",
      visionOn: "Ava Vision: Activado",
      visionOff: "Ava Vision: Desactivado",
      motionPaidAlert:
        "El seguimiento de movimiento de Ava está disponible con una suscripción AVAAIO de pago.",
      motionPaidTitle:
        "El seguimiento de movimiento de Ava requiere una suscripción AVAAIO de pago.",
      disableMotion: "Desactivar seguimiento de movimiento",
      enableMotion: "Activar seguimiento de movimiento",
      motionLocked: "Movimiento de Ava: Bloqueado",
      motionOn: "Movimiento de Ava: Activado",
      motionOff: "Movimiento de Ava: Desactivado",
      logout: "Cerrar sesión",
    },
    chat: {
      disclaimer:
        "Las respuestas de la IA pueden ser inexactas. Verifica la información importante.",
      logout: "Cerrar sesión",
      menuTitle: "MENÚ DE AVA",
      profile: "Perfil",
      account: "Cuenta",
      entertainment: "Entretenimiento",
      games: "Juegos",
      themeColors: "Tema y colores",
      logoutReturn: "Cerrar sesión y volver al inicio",
      inputPlaceholder: "Escribe tu mensaje...",
      thinking: "Pensando",
      thinkingTitle: "Ava está pensando",
      offlineMicError:
        "El micrófono sin conexión tuvo un error durante la grabación. Inténtalo de nuevo.",
      wakeStandbyTitle:
        "Palabra de activación en espera — di «Ava, ¿puedes oírme?»",
      micOffTitle: "Apagar el micrófono",
      micOnTitle:
        "Encender el micrófono y activar la palabra de activación",
      locale: "es-MX",
    },
    history: {
      ariaLabel: "Historial del chat",
      title: "Flujo de memoria de Ava",
      sessionCount:
        "{filtered} / {total} mensajes en esta sesión",
      searchPlaceholder:
        "Buscar en esta conversación...",
      noMatches:
        "Aún no hay historial de chat coincidente.",
      you: "Tú",
      ava: "Ava",
      close: "Cerrar",
    },
    bottomNav: {
      backSettings: "← Volver a Ajustes",
      backDashboard: "Volver al panel",
    },
    accountMenu: {
      options: "Opciones de cuenta",
      maintenance: "Mantenimiento",
      confirm:
        "Esto desconectará tu cuenta, eliminará la configuración local del perfil de Ava y cerrará tu sesión. ¿Continuar?",
      disconnect: "Desconectar y borrar — Cerrar sesión",
      detail:
        "Elimina el perfil local de Ava y borra la sesión de este dispositivo.",
    },
  },

  fr: {
    dashboard: {
      loadingSystem: "SYSTÈME D'INTELLIGENCE NEURONALE",
      loadingAva: "CHARGEMENT D'AVA...",
      lockedFeature:
        "{feature} est verrouillé avec l'offre Gratuite. Passez à Pro pour le déverrouiller.",
    },
    settings: {
      eyebrow: "Système de contrôle AVAAIO",
      title: "PARAMÈTRES",
      subtitle:
        "Configurez votre environnement AVAAIO, ouvrez les modules connectés et gérez votre compte depuis une seule interface.",
      lockedNotice:
        "🔒 Une mise à niveau est requise pour accéder à cette fonction",
      backDashboard: "← Retour au tableau de bord AVAAIO",
      cards: {
        profile: {
          title: "Profil",
          subtitle:
            "Consultez l'identité du compte, les informations affichées et les données utilisateur.",
        },
        system: {
          title: "Système",
          subtitle:
            "Gérez les paramètres système, la session, les modules connectés et les accès.",
        },
        account: {
          title: "Compte",
          subtitle:
            "Gérez la connexion, l'adresse e-mail, le mot de passe, l'abonnement et la sécurité.",
        },
        language: {
          title: "Langue et région",
          subtitle:
            "Choisissez la langue mémorisée par AVAAIO pour votre compte et l'interface.",
        },
        help: {
          title: "AIDE",
          subtitle:
            "Ouvrez l'aide, les guides et les ressources de la plateforme.",
        },
        support: {
          title: "Assistance",
          subtitle:
            "Contactez l'assistance et ouvrez les options d'aide.",
        },
        theme: {
          title: "Studio de thèmes",
          subtitle:
            "Réglez l'apparence de la plateforme et les commandes visuelles du tableau de bord.",
          freeSubtitle:
            "Passez à Pro pour déverrouiller la personnalisation visuelle d'Ava.",
        },
        entertainment: {
          title: "Divertissement",
          subtitle:
            "Ouvrez les modules multimédias et les expériences interactives.",
        },
        games: {
          title: "Centre de jeux",
          subtitle:
            "Lancez les échecs, les dames et d'autres modes de jeu.",
        },
        education: {
          title: "Éducation",
          subtitle:
            "Apprenez avec Ava grâce au tutorat, aux cartes, aux mathématiques et aux leçons guidées.",
        },
      },
    },
    language: {
      eyebrow: "SYSTÈME LINGUISTIQUE AVAAIO",
      title: "Langue et région",
      subtitle:
        "Choisissez la langue qu'AVAAIO doit mémoriser pour votre compte et l'interface.",
      preferred: "Langue préférée",
      selected: "Sélection",
      note:
        "Ce réglage ne change pas la langue de conversation ou du microphone d'Ava. Les conversations multilingues et le changement automatique de langue sont disponibles avec Pro.",
      saving: "Enregistrement...",
      saved: "Langue enregistrée",
      save: "Enregistrer la langue",
      back: "← Retour aux paramètres",
      success:
        "Préférence linguistique enregistrée. Application en cours...",
      error:
        "La préférence linguistique n'a pas pu être enregistrée.",
    },
    plan: {
      accountFallback: "Compte",
      title: "Accès au compte",
      statusPrefix: "État de",
      subtitle:
        "Les utilisateurs de l'offre Gratuite peuvent voir ici les fonctions verrouillées. Pro déverrouille Ava Vision, des fonctions étendues et un accès accru.",
      freeTitle: "Gratuit",
      freeDescription:
        "Accès limité à Ava, fonctions premium verrouillées et utilisation restreinte. Adapté pour commencer et découvrir les fonctions Pro.",
      proTitle: "Pro",
      proDescription:
        "Déverrouillez Ava Vision, davantage de fonctions, un accès étendu au compte et l'expérience premium Ava.",
      adminTitle: "Compte",
        adminDescription: "L’accès disponible pour ce compte est actif.",
      active: "Actif",
      unlockPro: "Déverrouiller Pro",
      iosOnly:
        "Les achats sont disponibles uniquement dans l'application iOS.",
      purchaseError: "Impossible de démarrer l'achat.",
    },
    mobile: {
      openControls: "Ouvrir les commandes d'Ava",
      controls: "Commandes d'Ava",
      profile: "Profil",
      history: "Historique",
      system: "Système",
      visionLockedTitle:
        "Ava Vision est verrouillé avec l'offre Gratuite. Passez à Pro pour le déverrouiller.",
      disableVision: "Désactiver Ava Vision",
      enableVision: "Activer Ava Vision",
      visionLocked: "Ava Vision : Verrouillé",
      visionOn: "Ava Vision : Activé",
      visionOff: "Ava Vision : Désactivé",
      motionPaidAlert:
        "Le suivi des mouvements d'Ava est disponible avec un abonnement AVAAIO payant.",
      motionPaidTitle:
        "Le suivi des mouvements d'Ava nécessite un abonnement AVAAIO payant.",
      disableMotion: "Désactiver le suivi des mouvements",
      enableMotion: "Activer le suivi des mouvements",
      motionLocked: "Mouvement d'Ava : Verrouillé",
      motionOn: "Mouvement d'Ava : Activé",
      motionOff: "Mouvement d'Ava : Désactivé",
      logout: "Déconnexion",
    },
    chat: {
      disclaimer:
        "Les réponses de l'IA peuvent être inexactes. Vérifiez les informations importantes.",
      logout: "Déconnexion",
      menuTitle: "MENU D'AVA",
      profile: "Profil",
      account: "Compte",
      entertainment: "Divertissement",
      games: "Jeux",
      themeColors: "Thème et couleurs",
      logoutReturn: "Se déconnecter et revenir à la connexion",
      inputPlaceholder: "Saisissez votre message...",
      thinking: "Réflexion en cours",
      thinkingTitle: "Ava réfléchit",
      offlineMicError:
        "Le microphone hors ligne a rencontré une erreur pendant l’enregistrement. Réessayez.",
      wakeStandbyTitle:
        "Mot d’activation en attente — dites « Ava, est-ce que tu m’entends ? »",
      micOffTitle: "Désactiver le microphone",
      micOnTitle:
        "Activer le microphone et le mot d’activation",
      locale: "fr-FR",
    },
    history: {
      ariaLabel: "Historique du chat",
      title: "Flux de mémoire d’Ava",
      sessionCount:
        "{filtered} / {total} messages dans cette session",
      searchPlaceholder:
        "Rechercher dans cette conversation...",
      noMatches:
        "Aucun historique correspondant pour le moment.",
      you: "Vous",
      ava: "Ava",
      close: "Fermer",
    },
    bottomNav: {
      backSettings: "← Retour aux paramètres",
      backDashboard: "Retour au tableau de bord",
    },
    accountMenu: {
      options: "Options du compte",
      maintenance: "Maintenance",
      confirm:
        "Cette action déconnectera votre compte, supprimera les réglages locaux du profil Ava et vous déconnectera. Continuer ?",
      disconnect: "Déconnecter et effacer — Se déconnecter",
      detail:
        "Supprime le profil Ava local et efface la session de cet appareil.",
    },
  },

  de: {
    dashboard: {
      loadingSystem: "NEURONALES INTELLIGENZSYSTEM",
      loadingAva: "AVA WIRD GELADEN...",
      lockedFeature:
        "{feature} ist im kostenlosen Tarif gesperrt. Wechseln Sie zu Pro, um die Funktion freizuschalten.",
    },
    settings: {
      eyebrow: "AVAAIO-Steuerungssystem",
      title: "EINSTELLUNGEN",
      subtitle:
        "Konfigurieren Sie Ihre AVAAIO-Umgebung, öffnen Sie verbundene Module und verwalten Sie Ihr Konto über eine zentrale Oberfläche.",
      lockedNotice:
        "🔒 Für diese Funktion ist ein Upgrade erforderlich",
      backDashboard: "← Zurück zum AVAAIO-Dashboard",
      cards: {
        profile: {
          title: "Profil",
          subtitle:
            "Zeigen Sie Kontoidentität, Anzeigeinformationen und Benutzerdaten an.",
        },
        system: {
          title: "System",
          subtitle:
            "Verwalten Sie Systemeinstellungen, Sitzung, verbundene Module und Zugriffe.",
        },
        account: {
          title: "Konto",
          subtitle:
            "Verwalten Sie Anmeldung, E-Mail, Passwort, Mitgliedschaft und Kontosicherheit.",
        },
        language: {
          title: "Sprache und Region",
          subtitle:
            "Wählen Sie die Sprache, die AVAAIO für Konto und Oberfläche speichert.",
        },
        help: {
          title: "HILFE",
          subtitle:
            "Öffnen Sie Hilfe, Anleitungen und Plattformressourcen.",
        },
        support: {
          title: "Support",
          subtitle:
            "Kontaktieren Sie den Support und öffnen Sie Hilfeoptionen.",
        },
        theme: {
          title: "Design-Studio",
          subtitle:
            "Passen Sie das Erscheinungsbild der Plattform und die Dashboard-Darstellung an.",
          freeSubtitle:
            "Wechseln Sie zu Pro, um Avas Designanpassung freizuschalten.",
        },
        entertainment: {
          title: "Unterhaltung",
          subtitle:
            "Öffnen Sie Medienmodule und interaktive Erlebnisse.",
        },
        games: {
          title: "Spielezentrum",
          subtitle:
            "Starten Sie Schach, Dame und weitere Spielmodi.",
        },
        education: {
          title: "Bildung",
          subtitle:
            "Lernen Sie mit Ava durch Nachhilfe, Lernkarten, Mathematik und geführte Lektionen.",
        },
      },
    },
    language: {
      eyebrow: "AVAAIO-SPRACHSYSTEM",
      title: "Sprache und Region",
      subtitle:
        "Wählen Sie die Sprache, die AVAAIO für Ihr Konto und die Oberfläche speichern soll.",
      preferred: "Bevorzugte Sprache",
      selected: "Ausgewählt",
      note:
        "Diese Einstellung ändert nicht die Gesprächs- oder Mikrofonsprache von Ava. Mehrsprachige Gespräche und automatische Sprachwechsel sind mit Pro verfügbar.",
      saving: "Wird gespeichert...",
      saved: "Sprache gespeichert",
      save: "Sprache speichern",
      back: "← Zurück zu Einstellungen",
      success:
        "Spracheinstellung gespeichert. Sie wird jetzt angewendet...",
      error:
        "Die Spracheinstellung konnte nicht gespeichert werden.",
    },
    plan: {
      accountFallback: "Konto",
      title: "Kontozugriff",
      statusPrefix: "Status für",
      subtitle:
        "Nutzer des kostenlosen Tarifs sehen hier gesperrte Funktionen. Pro schaltet Ava Vision, erweiterte Funktionen und höhere Nutzung frei.",
      freeTitle: "Kostenlos",
      freeDescription:
        "Eingeschränkter Ava-Zugriff, gesperrte Premiumfunktionen und begrenzte Nutzung. Geeignet für den Einstieg und eine Vorschau auf Pro.",
      proTitle: "Pro",
      proDescription:
        "Schalten Sie Ava Vision, weitere Funktionen, erweiterten Kontozugriff und das Premium-Erlebnis frei.",
      adminTitle: "Konto",
        adminDescription: "Der für dieses Konto verfügbare Zugriff ist aktiv.",
      active: "Aktiv",
      unlockPro: "Pro freischalten",
      iosOnly:
        "Käufe sind nur in der iOS-App verfügbar.",
      purchaseError: "Der Kauf konnte nicht gestartet werden.",
    },
    mobile: {
      openControls: "Ava-Steuerung öffnen",
      controls: "Ava-Steuerung",
      profile: "Profil",
      history: "Verlauf",
      system: "System",
      visionLockedTitle:
        "Ava Vision ist im kostenlosen Tarif gesperrt. Wechseln Sie zu Pro, um es freizuschalten.",
      disableVision: "Ava Vision deaktivieren",
      enableVision: "Ava Vision aktivieren",
      visionLocked: "Ava Vision: Gesperrt",
      visionOn: "Ava Vision: Aktiv",
      visionOff: "Ava Vision: Aus",
      motionPaidAlert:
        "Avas Bewegungsverfolgung ist mit einem kostenpflichtigen AVAAIO-Abonnement verfügbar.",
      motionPaidTitle:
        "Avas Bewegungsverfolgung erfordert ein kostenpflichtiges AVAAIO-Abonnement.",
      disableMotion: "Bewegungsverfolgung deaktivieren",
      enableMotion: "Bewegungsverfolgung aktivieren",
      motionLocked: "Ava-Bewegung: Gesperrt",
      motionOn: "Ava-Bewegung: Aktiv",
      motionOff: "Ava-Bewegung: Aus",
      logout: "Abmelden",
    },
    chat: {
      disclaimer:
        "KI-Antworten können ungenau sein. Prüfen Sie wichtige Informationen.",
      logout: "Abmelden",
      menuTitle: "AVA-MENÜ",
      profile: "Profil",
      account: "Konto",
      entertainment: "Unterhaltung",
      games: "Spiele",
      themeColors: "Design und Farben",
      logoutReturn: "Abmelden und zur Anmeldung zurückkehren",
      inputPlaceholder: "Nachricht eingeben...",
      thinking: "Denkt nach",
      thinkingTitle: "Ava denkt nach",
      offlineMicError:
        "Beim Offline-Mikrofon ist während der Aufnahme ein Fehler aufgetreten. Versuchen Sie es erneut.",
      wakeStandbyTitle:
        "Aktivierungswort in Bereitschaft — sagen Sie „Ava, kannst du mich hören?“",
      micOffTitle: "Mikrofon ausschalten",
      micOnTitle:
        "Mikrofon einschalten und Aktivierungswort aktivieren",
      locale: "de-DE",
    },
    history: {
      ariaLabel: "Chatverlauf",
      title: "Avas Erinnerungsstrom",
      sessionCount:
        "{filtered} / {total} Nachrichten in dieser Sitzung",
      searchPlaceholder:
        "Diese Unterhaltung durchsuchen...",
      noMatches:
        "Noch kein passender Chatverlauf.",
      you: "Sie",
      ava: "Ava",
      close: "Schließen",
    },
    bottomNav: {
      backSettings: "← Zurück zu Einstellungen",
      backDashboard: "Zurück zum Dashboard",
    },
    accountMenu: {
      options: "Kontooptionen",
      maintenance: "Wartung",
      confirm:
        "Dadurch wird Ihr Konto getrennt, Avas lokale Profileinstellungen werden entfernt und Sie werden abgemeldet. Fortfahren?",
      disconnect: "Trennen und löschen — Abmelden",
      detail:
        "Entfernt das lokale Ava-Profil und löscht die Sitzung dieses Geräts.",
    },
  },

  it: {
    dashboard: {
      loadingSystem: "SISTEMA DI INTELLIGENZA NEURALE",
      loadingAva: "CARICAMENTO DI AVA...",
      lockedFeature:
        "{feature} è bloccato nel piano Gratuito. Passa a Pro per sbloccarlo.",
    },
    settings: {
      eyebrow: "Sistema di controllo AVAAIO",
      title: "IMPOSTAZIONI",
      subtitle:
        "Configura il tuo ambiente AVAAIO, apri i moduli collegati e gestisci i sistemi dell'account da un'unica interfaccia.",
      lockedNotice:
        "🔒 È richiesto un aggiornamento per accedere a questa funzione",
      backDashboard: "← Torna alla dashboard AVAAIO",
      cards: {
        profile: {
          title: "Profilo",
          subtitle:
            "Visualizza identità dell'account, informazioni mostrate e dati utente.",
        },
        system: {
          title: "Sistema",
          subtitle:
            "Gestisci impostazioni di sistema, sessione, moduli collegati e accessi.",
        },
        account: {
          title: "Account",
          subtitle:
            "Gestisci accesso, email, password, abbonamento e sicurezza dell'account.",
        },
        language: {
          title: "Lingua e area geografica",
          subtitle:
            "Scegli la lingua che AVAAIO ricorderà per l'account e l'interfaccia.",
        },
        help: {
          title: "AIUTO",
          subtitle:
            "Apri assistenza, guide e risorse della piattaforma.",
        },
        support: {
          title: "Supporto",
          subtitle:
            "Contatta il supporto e apri le opzioni di assistenza.",
        },
        theme: {
          title: "Studio temi",
          subtitle:
            "Regola l'aspetto della piattaforma e i controlli visivi della dashboard.",
          freeSubtitle:
            "Passa a Pro per sbloccare la personalizzazione visiva di Ava.",
        },
        entertainment: {
          title: "Intrattenimento",
          subtitle:
            "Apri moduli multimediali ed esperienze interattive.",
        },
        games: {
          title: "Centro giochi",
          subtitle:
            "Avvia scacchi, dama e altre modalità di gioco.",
        },
        education: {
          title: "Istruzione",
          subtitle:
            "Impara con Ava attraverso tutoraggio, schede, matematica e lezioni guidate.",
        },
      },
    },
    language: {
      eyebrow: "SISTEMA LINGUISTICO AVAAIO",
      title: "Lingua e area geografica",
      subtitle:
        "Scegli la lingua che AVAAIO deve ricordare per il tuo account e l'interfaccia.",
      preferred: "Lingua preferita",
      selected: "Selezionata",
      note:
        "Questa impostazione non modifica la lingua di conversazione o del microfono di Ava. Le conversazioni multilingue e il cambio automatico della lingua sono disponibili con Pro.",
      saving: "Salvataggio...",
      saved: "Lingua salvata",
      save: "Salva lingua",
      back: "← Torna alle Impostazioni",
      success:
        "Preferenza della lingua salvata. Applicazione in corso...",
      error:
        "Non è stato possibile salvare la preferenza della lingua.",
    },
    plan: {
      accountFallback: "Account",
      title: "Accesso account",
      statusPrefix: "Stato di",
      subtitle:
        "Le funzioni disponibili sono mostrate di seguito in base al piano AVAAIO attuale.",
      freeTitle: "Gratuito",
      freeDescription:
        "Accesso limitato ad Ava, funzioni premium bloccate e utilizzo ridotto. Adatto per iniziare e vedere le funzioni Pro.",
      proTitle: "Pro",
      proDescription:
        "Sblocca Ava Vision, più funzioni, maggiore accesso all'account e l'esperienza premium di Ava.",
      adminTitle: "Account",
        adminDescription: "L’accesso disponibile per questo account è attivo.",
      active: "Attivo",
      unlockPro: "Sblocca Pro",
      iosOnly:
        "Gli acquisti sono disponibili solo nell'app iOS.",
      purchaseError: "Impossibile avviare l'acquisto.",
    },
    mobile: {
      openControls: "Apri i controlli di Ava",
      controls: "Controlli di Ava",
      profile: "Profilo",
      history: "Cronologia",
      system: "Sistema",
      visionLockedTitle:
        "Ava Vision è bloccato nel piano Gratuito. Passa a Pro per sbloccarlo.",
      disableVision: "Disattiva Ava Vision",
      enableVision: "Attiva Ava Vision",
      visionLocked: "Ava Vision: Bloccato",
      visionOn: "Ava Vision: Attivo",
      visionOff: "Ava Vision: Disattivato",
      motionPaidAlert:
        "Il tracciamento del movimento di Ava è disponibile con un abbonamento AVAAIO a pagamento.",
      motionPaidTitle:
        "Il tracciamento del movimento di Ava richiede un abbonamento AVAAIO a pagamento.",
      disableMotion: "Disattiva tracciamento movimento",
      enableMotion: "Attiva tracciamento movimento",
      motionLocked: "Movimento Ava: Bloccato",
      motionOn: "Movimento Ava: Attivo",
      motionOff: "Movimento Ava: Disattivato",
      logout: "Esci",
    },
    chat: {
      disclaimer:
        "Le risposte dell'IA possono essere inesatte. Verifica le informazioni importanti.",
      logout: "Esci",
      menuTitle: "MENU AVA",
      profile: "Profilo",
      account: "Account",
      entertainment: "Intrattenimento",
      games: "Giochi",
      themeColors: "Tema e colori",
      logoutReturn: "Esci e torna alla schermata di accesso",
      inputPlaceholder: "Scrivi il tuo messaggio...",
      thinking: "Sto pensando",
      thinkingTitle: "Ava sta pensando",
      offlineMicError:
        "Si è verificato un errore del microfono offline durante la registrazione. Riprova.",
      wakeStandbyTitle:
        "Parola di attivazione in attesa — di’ «Ava, riesci a sentirmi?»",
      micOffTitle: "Disattiva il microfono",
      micOnTitle:
        "Attiva il microfono e la parola di attivazione",
      locale: "it-IT",
    },
    history: {
      ariaLabel: "Cronologia chat",
      title: "Flusso di memoria di Ava",
      sessionCount:
        "{filtered} / {total} messaggi in questa sessione",
      searchPlaceholder:
        "Cerca in questa conversazione...",
      noMatches:
        "Non è ancora presente una cronologia corrispondente.",
      you: "Tu",
      ava: "Ava",
      close: "Chiudi",
    },
    bottomNav: {
      backSettings: "← Torna alle Impostazioni",
      backDashboard: "Torna alla dashboard",
    },
    accountMenu: {
      options: "Opzioni account",
      maintenance: "Manutenzione",
      confirm:
        "Questa operazione disconnetterà l'account, rimuoverà le impostazioni locali del profilo Ava e terminerà la sessione. Continuare?",
      disconnect: "Disconnetti e cancella — Esci",
      detail:
        "Rimuove il profilo Ava locale e cancella la sessione del dispositivo.",
    },
  },

  pt: {
    dashboard: {
      loadingSystem: "SISTEMA DE INTELIGÊNCIA NEURAL",
      loadingAva: "CARREGANDO A AVA...",
      lockedFeature:
        "{feature} está bloqueado no plano Gratuito. Atualize para o Pro para desbloquear.",
    },
    settings: {
      eyebrow: "Sistema de controle AVAAIO",
      title: "CONFIGURAÇÕES",
      subtitle:
        "Configure seu ambiente AVAAIO, abra os módulos conectados e gerencie os sistemas da conta em uma única interface.",
      lockedNotice:
        "🔒 É necessário fazer upgrade para acessar este recurso",
      backDashboard: "← Voltar ao painel AVAAIO",
      cards: {
        profile: {
          title: "Perfil",
          subtitle:
            "Veja a identidade da conta, as informações exibidas e os dados do usuário.",
        },
        system: {
          title: "Sistema",
          subtitle:
            "Gerencie configurações do sistema, sessão, módulos conectados e acesso.",
        },
        account: {
          title: "Conta",
          subtitle:
            "Gerencie login, email, senha, assinatura e controles de segurança da conta.",
        },
        language: {
          title: "Idioma e região",
          subtitle:
            "Escolha o idioma que o AVAAIO lembrará para sua conta e interface.",
        },
        help: {
          title: "AJUDA",
          subtitle:
            "Abra ajuda, orientações e recursos da plataforma.",
        },
        support: {
          title: "Suporte",
          subtitle:
            "Entre em contato com o suporte e abra as opções de assistência.",
        },
        theme: {
          title: "Estúdio de temas",
          subtitle:
            "Ajuste a aparência da plataforma e os controles visuais do painel.",
          freeSubtitle:
            "Atualize para o Pro para desbloquear a personalização visual da Ava.",
        },
        entertainment: {
          title: "Entretenimento",
          subtitle:
            "Abra módulos de mídia e experiências interativas.",
        },
        games: {
          title: "Central de jogos",
          subtitle:
            "Inicie xadrez, damas e outros modos de jogo.",
        },
        education: {
          title: "Educação",
          subtitle:
            "Aprenda com a Ava por meio de tutoria, cartões, matemática e aulas guiadas.",
        },
      },
    },
    language: {
      eyebrow: "SISTEMA DE IDIOMAS AVAAIO",
      title: "Idioma e região",
      subtitle:
        "Escolha o idioma que o AVAAIO deve lembrar para sua conta e interface.",
      preferred: "Idioma preferido",
      selected: "Selecionado",
      note:
        "Esta configuração não altera o idioma de conversa ou do microfone da Ava. Conversas multilíngues e mudança automática de idioma estão disponíveis com o Pro.",
      saving: "Salvando...",
      saved: "Idioma salvo",
      save: "Salvar idioma",
      back: "← Voltar às Configurações",
      success:
        "Preferência de idioma salva. Aplicando agora...",
      error:
        "Não foi possível salvar a preferência de idioma.",
    },
    plan: {
      accountFallback: "Conta",
      title: "Acesso da conta",
      statusPrefix: "Status de",
      subtitle:
        "Usuários do plano Gratuito podem visualizar os recursos bloqueados. O Pro desbloqueia Ava Vision, recursos ampliados e maior acesso de uso.",
      freeTitle: "Gratuito",
      freeDescription:
        "Acesso limitado à Ava, recursos premium bloqueados e uso restrito. Adequado para começar e conhecer os recursos Pro.",
      proTitle: "Pro",
      proDescription:
        "Desbloqueie Ava Vision, mais recursos, maior acesso à conta e a experiência premium da Ava.",
      adminTitle: "Conta",
        adminDescription: "O acesso disponível para esta conta está ativo.",
      active: "Ativo",
      unlockPro: "Desbloquear Pro",
      iosOnly:
        "As compras estão disponíveis somente no aplicativo iOS.",
      purchaseError: "Não foi possível iniciar a compra.",
    },
    mobile: {
      openControls: "Abrir controles da Ava",
      controls: "Controles da Ava",
      profile: "Perfil",
      history: "Histórico",
      system: "Sistema",
      visionLockedTitle:
        "Ava Vision está bloqueado no plano Gratuito. Atualize para o Pro para desbloquear.",
      disableVision: "Desativar Ava Vision",
      enableVision: "Ativar Ava Vision",
      visionLocked: "Ava Vision: Bloqueado",
      visionOn: "Ava Vision: Ativado",
      visionOff: "Ava Vision: Desativado",
      motionPaidAlert:
        "O rastreamento de movimento da Ava está disponível com uma assinatura AVAAIO paga.",
      motionPaidTitle:
        "O rastreamento de movimento da Ava exige uma assinatura AVAAIO paga.",
      disableMotion: "Desativar rastreamento de movimento",
      enableMotion: "Ativar rastreamento de movimento",
      motionLocked: "Movimento da Ava: Bloqueado",
      motionOn: "Movimento da Ava: Ativado",
      motionOff: "Movimento da Ava: Desativado",
      logout: "Sair",
    },
    chat: {
      disclaimer:
        "As respostas da IA podem estar incorretas. Verifique informações importantes.",
      logout: "Sair",
      menuTitle: "MENU DA AVA",
      profile: "Perfil",
      account: "Conta",
      entertainment: "Entretenimento",
      games: "Jogos",
      themeColors: "Tema e cores",
      logoutReturn: "Sair e voltar à tela de login",
      inputPlaceholder: "Digite sua mensagem...",
      thinking: "Pensando",
      thinkingTitle: "Ava está pensando",
      offlineMicError:
        "O microfone offline encontrou um erro durante a gravação. Tente novamente.",
      wakeStandbyTitle:
        "Palavra de ativação em espera — diga “Ava, você consegue me ouvir?”",
      micOffTitle: "Desligar o microfone",
      micOnTitle:
        "Ligar o microfone e ativar a palavra de ativação",
      locale: "pt-BR",
    },
    history: {
      ariaLabel: "Histórico do chat",
      title: "Fluxo de memória da Ava",
      sessionCount:
        "{filtered} / {total} mensagens nesta sessão",
      searchPlaceholder:
        "Pesquisar nesta conversa...",
      noMatches:
        "Ainda não há histórico de chat correspondente.",
      you: "Você",
      ava: "Ava",
      close: "Fechar",
    },
    bottomNav: {
      backSettings: "← Voltar às Configurações",
      backDashboard: "Voltar ao painel",
    },
    accountMenu: {
      options: "Opções da conta",
      maintenance: "Manutenção",
      confirm:
        "Isto desconectará sua conta, removerá as configurações locais do perfil da Ava e encerrará sua sessão. Continuar?",
      disconnect: "Desconectar e apagar — Sair",
      detail:
        "Remove o perfil local da Ava e limpa a sessão deste dispositivo.",
    },
  },
} as const;

export function getAvaaioUiCopy(
  value: unknown
) {
  const language: AvaaioLanguageCode =
    normalizeAvaaioLanguage(value);

  return AVAAIO_UI_COPY[language];
}
